import type { ColumnDef } from '@tanstack/react-table';
import {
  Card,
  InputDateTime,
  InputSelect,
  InputText,
  TableListData,
} from 'components';
import {
  Button,
  InputWraper,
  useForm,
  NumberUtils,
  IUseFormStructureItem,
} from 'modules';
import React, { useCallback, useState } from 'react';
import {
  useGetPointTransactionsQuery,
  useExportPointTransactions,
} from 'services';
import type {
  GetListPointTransactionEntity,
  DetailPointTransactionEntity,
} from 'services/openapi';
import { DEFAULT_PAGE } from 'configs';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import {
  formatColumn,
  formatRowData,
  formatRowDataTotal,
} from 'configs/cell_xlsx';

import {
  columnsDefined,
  convertSelectStatusOptions,
  convertSelectTransactionTypeOptions,
  convertVariables,
  type FilterType,
  getInitFilterValues,
  exportHeaders,
} from './config';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

const PointTransactionsList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'transactionCode',
      label: 'Mã giao dịch',
      defaultValue: initFilterValues.transactionCode,
    },
    {
      name: 'transactionStatus',
      label: 'Trạng thái',
      defaultValue: initFilterValues.transactionStatus,
      validate: Yup.string().required('Vui lòng chọn Trạng thái'),
    },
    {
      name: 'transactionType',
      label: 'Loại giao dịch',
      defaultValue: initFilterValues.transactionType,
    },
    {
      name: 'fromUserSearchString',
      label: 'Bên chuyển',
    },
    {
      name: 'toUserSearchString',
      label: 'Bên thụ hưởng',
    },
    {
      name: 'fromTime',
      label: 'Ngày & giờ bắt đầu',
      defaultValue: initFilterValues.fromTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ bắt đầu')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ bắt đầu phải nhỏ hơn Ngày & giờ kết thúc',
          function (value) {
            if (value)
              return (
                new Date(value).getTime() <
                new Date(this.parent?.toTime).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(this.parent?.toTime);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'toTime',
      label: 'Ngày & giờ kết thúc',
      defaultValue: initFilterValues.toTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ kết thúc')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ kết thúc phải lớn hơn Ngày & giờ bắt đầu',
          function (value) {
            if (value)
              return (
                new Date(this.parent?.fromTime).getTime() <
                new Date(value).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(this.parent?.fromTime);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const normalizedPhoneNumber = (phoneNumber: string) => {
    return phoneNumber.replace(/\+84/, '0');
  };

  const normalizeData = (data: GetListPointTransactionEntity) => {
    const dataSummary: any = data?.summary?.find(
      (summary: any) => summary.title === 'All',
    );
    return dataSummary.total;
  };

  const { isFetching: isLoadingExport, refetch } = useExportPointTransactions({
    ...convertVariables(variables),
    page: '1',
    pageSize: String(
      (dataTable as unknown as GetListPointTransactionEntity)?.total,
    ),
  });

  const handleExportExcel = async () => {
    const allData = await refetch({
      ...convertVariables(variables),
      page: DEFAULT_PAGE,
      pageSize: String(
        (dataTable as unknown as GetListPointTransactionEntity)?.total,
      ),
    } as any);

    const dataArray = allData?.data?.data;

    const result = Array.isArray(dataArray) ? dataArray : [];

    //
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Columns
    const headerColumn = exportHeaders.map((header) => {
      return {
        header: header,
        key: header,
        width: 40,
      };
    });
    worksheet.columns = headerColumn;

    const firstRow = worksheet.getRow(1);
    firstRow.eachCell((cell) => formatColumn(cell));

    // Data
    result.map((item: DetailPointTransactionEntity, index: number) => {
      const row = worksheet.addRow([
        index + 1,
        item.transactionCode,
        item.transactionType,
        item.serviceType,
        normalizedPhoneNumber(item.fromUserPhone),
        normalizedPhoneNumber(item.toUserPhone),
        NumberUtils.toFormatNumber(item.amount),
        item.transactionStatusName,
        format(new Date(String(item.transactionUpdAt)), 'HH:mm:ss dd/MM/yyyy'),
      ]);
      row.eachCell((cell) => formatRowData(cell));
    });

    // Total
    const getValueSumAmount = NumberUtils.toFormatNumber(
      Number(normalizeData(allData.data as GetListPointTransactionEntity)),
    );
    const row = worksheet.addRow([
      '',
      '',
      '',
      '',
      '',
      'TỔNG',
      getValueSumAmount,
      '',
      '',
    ]);
    row.eachCell((cell, colNumber) => {
      if (colNumber === 6 || colNumber === 7) {
        formatRowDataTotal(cell);
      }
    });

    // Generate Excel and trigger file download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const now = new Date();
    saveAs(
      blob,
      `Danh_sach_cong_tru_diem_${now.toLocaleTimeString('vn-VN', { hour12: false }).replace(/:/g, '_')}_${now.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '_')}.xlsx`,
    );
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('transactionCode')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('transactionType')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectTransactionTypeOptions()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('transactionStatus')}
                isRequired
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectStatusOptions()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('fromTime')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('toTime')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('fromUserSearchString')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('toUserSearchString')}
                renderInput={InputText}
              />
            </div>
          </div>
          <div className='textCenter mt16 wrapButtonFilter'>
            <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
            <Button
              className='ml15'
              onClick={handleExportExcel}
              label={'Tải về DS'}
              type='submit'
              buttonType='info'
              isLoading={isLoadingExport}
            />
          </div>
        </form>
      </Card>

      <TableListData<GetListPointTransactionEntity>
        columns={columnsDefined as ColumnDef<GetListPointTransactionEntity>[]}
        useQuery={useGetPointTransactionsQuery}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        filterKey='searchDetailContent'
        orderColumn
        callBackData={setDataTable}
        customTopRow={(data) => {
          if (data.summary) {
            return (
              <>
                <tr>
                  <td colSpan={6}></td>
                  <td>
                    <strong>TỔNG</strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(Number(normalizeData(data)))}
                    </strong>
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              </>
            );
          }
        }}
      />
    </>
  );
};

export default PointTransactionsList;
